import { useEffect, useState } from 'react'
import type { MediaSettings } from '@alpha-edge/shared'
import { PageHeader, FormField, Alert, btnPrimary, input, colors } from '../../components/ui'
import { api, ApiError } from '../../services/api'

export function MediaSettingsPage() {
  const [settingsJson, setSettingsJson] = useState('{}')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .get<MediaSettings>('/media-settings')
      .then((res) => {
        if (cancelled) return
        if (res.data) setSettingsJson(JSON.stringify(res.data.settings ?? {}, null, 2))
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function handleSave() {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      let settings: unknown
      try {
        settings = JSON.parse(settingsJson || '{}')
      } catch (err) {
        setError(`JSON invalid: ${err instanceof Error ? err.message : String(err)}`)
        setSaving(false)
        return
      }
      await api.put<MediaSettings>('/media-settings', { settings })
      setSuccess('Saved.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted }}>Loading...</div>

  return (
    <div>
      <PageHeader title="MEDIA Settings" />
      <p style={{ color: colors.textMuted, fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Organization-specific configuration bundle. V1 stores the settings as a free-form JSON document; typed columns
        will be carved out of this blob as concrete requirements firm up (coding rules, panel intervals, etc.).
      </p>
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}
      <FormField name="settings" label="Settings (JSON)">
        <textarea
          style={{
            ...input,
            minHeight: '360px',
            resize: 'vertical',
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
          }}
          value={settingsJson}
          onChange={(e) => setSettingsJson(e.target.value)}
        />
      </FormField>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
        <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
